import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import Icon from '../components/ui/Icon'
import AuthGuard from '../components/AuthGuard'
import apiService from '../services/api'

const VerifyOTP = () => {
  return (
    <AuthGuard>
      {({ user, logout, openAuth, refreshUser }) => (
        <VerifyOTPContent user={user} logout={logout} openAuth={openAuth} refreshUser={refreshUser} />
      )}
    </AuthGuard>
  )
}

const VerifyOTPContent = ({ user, logout, openAuth, refreshUser }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [step, setStep] = useState('confirm') // 'confirm' | 'verify' (SMS) | 'call'
  const [phoneNumber, setPhoneNumber] = useState(user?.phone_number || '')
  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const [callCode, setCallCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const CALL_IN_NUMBER = '09644342002' // number users dial for call verification
  const [success, setSuccess] = useState('')
  const [timeLeft, setTimeLeft] = useState(0)

  useEffect(() => {
    // If user is already verified, redirect to profile immediately
    if (user?.otp_verified) {
      navigate('/profile', { replace: true })
    }
    // Set phone number from user data
    if (user?.phone_number && !phoneNumber) {
      setPhoneNumber(user.phone_number)
    }
  }, [user, navigate, phoneNumber])

  useEffect(() => {
    // Countdown timer
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [timeLeft])

  useEffect(() => {
    // Call code expired → close the call screen and return to the start.
    if (step === 'call' && timeLeft === 0) {
      setStep('confirm')
      setCallCode('')
      setError('কোডের মেয়াদ শেষ হয়ে গেছে। অনুগ্রহ করে আবার চেষ্টা করুন।')
    }
  }, [step, timeLeft])

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const handleStartCall = async () => {
    setLoading(true)
    setError('')
    setSuccess('')

    try {
      const response = await apiService.initiateCallOtp()

      if (response.already_verified) {
        setError('আপনার নাম্বার ইতিমধ্যে ভেরিফাই করা আছে। আর ভেরিফিকেশনের প্রয়োজন নেই।')
        return
      }

      setCallCode(response.code)
      setTimeLeft(response.expires_in || 300)
      setStep('call')
    } catch (err) {
      setError(err.message || 'কল শুরু করা যায়নি। আবার চেষ্টা করুন।')
    } finally {
      setLoading(false)
    }
  }

  // While waiting on the call, poll until the number is verified.
  useEffect(() => {
    if (step !== 'call') return
    const poll = setInterval(async () => {
      try {
        const status = await apiService.getCallOtpStatus()
        if (status.verified || status.call_verified) {
          clearInterval(poll)
          setSuccess('আপনার নাম্বার ভেরিফাই সম্পন্ন হয়েছে!')
          if (refreshUser) await refreshUser()
          localStorage.removeItem('user_cache_timestamp')
          setTimeout(() => navigate('/profile', { replace: true, state: { showFajrReminder: true } }), 1500)
        }
      } catch {
        // transient error — keep polling
      }
    }, 3000)
    return () => clearInterval(poll)
  }, [step, refreshUser, navigate])

  const handleOTPChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp]
      newOtp[index] = value
      setOtp(newOtp)

      // Auto-focus next input
      if (value && index < 5) {
        document.getElementById(`otp-${index + 1}`)?.focus()
      }

      // Don't auto-submit, let user click verify button
    }
  }

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus()
    }
  }

  const verifyOTP = async (otpCode = null) => {
    const code = otpCode || otp.join('')

    if (code.length !== 6) {
      setError('Please enter all 6 digits')
      return
    }

    setLoading(true)
    setError('')

    try {
      await apiService.verifyOtp(phoneNumber, code)
      setSuccess('Phone number verified successfully!')

      // Refresh user data to get updated otp_verified status
      if (refreshUser) {
        await refreshUser()
      }

      // Clear any cached data and redirect immediately
      localStorage.removeItem('user_cache_timestamp')

      // Use replace to prevent back navigation
      setTimeout(() => {
        navigate('/profile', { replace: true, state: { showFajrReminder: true } })
      }, 1500)
    } catch (err) {
      setError(err.message || 'Invalid OTP. Please try again.')
      setOtp(['', '', '', '', '', ''])
      document.getElementById('otp-0')?.focus()
    } finally {
      setLoading(false)
    }
  }

  // Prevent rendering OTP form if user is already verified
  if (user?.otp_verified) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar user={user} onLogout={logout} onAuth={openAuth} />
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-md mx-auto">
            <Card className="p-6">
              <div className="text-center">
                <Icon name="CheckCircle" className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <h1 className="text-2xl font-bold text-foreground">ইতিমধ্যে ভেরিফাইড</h1>
                <p className="text-muted-foreground mt-2 mb-4">
                  আপনার ফোন নাম্বার ইতিমধ্যে ভেরিফাই করা আছে
                </p>
                <Button
                  onClick={() => navigate('/profile', { replace: true })}
                  className="w-full"
                >
                  প্রোফাইলে ফিরে যান
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar user={user} onLogout={logout} onAuth={openAuth} />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-md mx-auto">
          <Card className="p-6">
            <div className="text-center mb-6">
              <Icon name="Shield" className="w-12 h-12 text-primary mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-foreground">ফোন নাম্বার ভেরিফিকেশন</h1>
              <p className="text-muted-foreground mt-2">
                সকল সেবা ব্যবহার করতে আপনার নাম্বার ভেরিফাই করুন
              </p>
            </div>

            {success && (
              <div className="bg-green-50 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                {success}
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}

            {step === 'confirm' && (
              <div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    আপনার ফোন নাম্বার
                  </label>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="font-semibold text-lg">{phoneNumber || user?.phone_number || t('common.loading')}</span>
                  </div>

                  <p className="text-sm text-muted-foreground mt-2">
                    নম্বর যাচাই করতে আমাদের নম্বরে কল করুন
                  </p>
                </div>

                <Button
                  onClick={handleStartCall}
                  disabled={loading || !phoneNumber}
                  className="w-full"
                >
                  <Icon name="Phone" className="w-4 h-4 mr-2" />
                  কল দিয়ে ভেরিফাই করুন
                </Button>
                <p className="text-xs text-center text-gray-500 mt-2">
                  আপনি আমাদের নম্বরে কল করে কোডটি চাপবেন
                </p>
              </div>
            )}

            {step === 'verify' && (
              <div>
                <div className="mb-6">
                  <p className="text-sm text-gray-600 mb-2">
                    {phoneNumber} নাম্বারে পাঠানো ৬ সংখ্যার কোড লিখুন
                  </p>

                  <div className="bg-yellow-50 border border-yellow-400 text-yellow-700 px-3 py-2 rounded mb-4">
                    <div className="flex items-center">
                      <Icon name="AlertCircle" className="w-4 h-4 mr-2" />
                      <span className="text-sm font-medium">নিশ্চিত করুন আপনার OTP সঠিক, আমরা দিনে একবারই OTP পাঠাই।</span>
                    </div>
                  </div>

                  <div className="flex justify-center space-x-2 mb-4">
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        id={`otp-${index}`}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOTPChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        className="w-12 h-12 text-center text-lg font-semibold border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                        autoFocus={index === 0}
                      />
                    ))}
                  </div>

                  {timeLeft > 0 && (
                    <p className="text-sm text-center text-gray-600">
                      সময় বাকি: <span className="font-semibold">{formatTime(timeLeft)}</span>
                    </p>
                  )}

                  {timeLeft === 0 && (
                    <p className="text-sm text-center text-red-600">
                      OTP মেয়াদ শেষ! আগামীকাল আবার চেষ্টা করুন।
                    </p>
                  )}
                </div>

                <div className="space-y-3">
                  <Button
                    onClick={() => verifyOTP()}
                    disabled={loading || otp.some(d => !d) || timeLeft === 0}
                    className="w-full"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        যাচাই করা হচ্ছে...
                      </>
                    ) : (
                      <>
                        <Icon name="Check" className="w-4 h-4 mr-2" />
                        ভেরিফাই করুন
                      </>
                    )}
                  </Button>

                  <Button
                    variant="outline"
                    onClick={() => setStep('confirm')}
                    className="w-full"
                  >
                    পূর্ববর্তী ধাপে ফিরে যান
                  </Button>
                </div>

                <p className="text-xs text-center text-gray-500 mt-4">
                  দিনে মাত্র একবার OTP পাঠানো যাবে
                </p>
              </div>
            )}

            {step === 'call' && (
              <div>
                <div className="text-center mb-6">
                  {/* Step 1 — the number to CALL (filled action button) */}
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-green-600 flex items-center justify-center"><Icon name="Phone" size={15} className="text-white" /></span>
                    <span className="text-sm font-semibold text-gray-800">প্রথমে এই নম্বরে কল করুন</span>
                  </div>
                  <a href={`tel:${CALL_IN_NUMBER}`}
                     className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white text-2xl font-bold tracking-wide rounded-xl py-3 mb-6 shadow-sm transition">
                    <Icon name="Phone" className="w-6 h-6" /> {CALL_IN_NUMBER}
                  </a>
                  {/* Step 2 — press 1, then enter the code (distinct boxed digits) */}
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-green-600 flex items-center justify-center"><Icon name="Smartphone" size={15} className="text-white" /></span>
                    <span className="text-sm font-semibold text-gray-800">এরপর মেনুতে <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-green-100 text-green-700 text-xs font-bold border border-green-300 align-middle">১</span> চাপুন, তারপর এই কোডটি চাপুন</span>
                  </div>
                  <p className="text-xs text-gray-400 mb-2">আপনার কোড</p>
                  <div className="flex justify-center gap-2 mb-3">
                    {String(callCode).split('').map((d, i) => (
                      <span key={i} className="w-12 h-14 flex items-center justify-center text-3xl font-bold bg-gray-50 border-2 border-gray-300 rounded-lg text-gray-800">{d}</span>
                    ))}
                  </div>

                  {timeLeft > 0 ? (
                    <p className="text-sm text-gray-600">
                      কোডের মেয়াদ বাকি: <span className="font-semibold">{formatTime(timeLeft)}</span>
                    </p>
                  ) : (
                    <p className="text-sm text-red-600">কোডের মেয়াদ শেষ। আবার চেষ্টা করুন।</p>
                  )}
                </div>

                <div className="bg-yellow-50 border border-yellow-300 text-yellow-800 text-xs rounded p-2 mb-4 text-center">
                  ⚠️ ফজর ফাইটার কখনো ফোনে এই কোড চাইবে না। কেউ কোড চাইলে দেবেন না।
                </div>

                <div className="flex items-center justify-center text-sm text-gray-500 mb-4">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
                  ভেরিফিকেশনের জন্য অপেক্ষা করা হচ্ছে...
                </div>

                <Button
                  variant="outline"
                  onClick={() => { setStep('confirm'); setCallCode(''); setTimeLeft(0) }}
                  className="w-full"
                >
                  বাতিল করুন
                </Button>
              </div>
            )}
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  )
}

export default VerifyOTP